// T2: Passive monitoring view with snapshot panels.
//
// Covers Stitch v2 design `intercom_monitoring_with_visual_snapshots`.
// Read-only — no answer/end actions; intended for security operators
// watching activity rather than handling calls. Snapshot IDs are
// rendered as image tiles via the existing snapshots endpoint.

import { Badge, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import { useIntercomCalls, statusLabel, statusTone } from '../lib/intercomCalls.js';

export function IntercomMonitoringPage() {
  const { calls, error, loading } = useIntercomCalls();

  if (loading) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  const active = (calls ?? []).filter(
    (c) => c.status === 'queued' || c.status === 'answered',
  );

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Intercom monitoring</h1>
        <p className="text-on-surface-variant">
          Passive observer view of every active call across the property. Auto-refreshes
          every 5 seconds.
        </p>
      </header>

      {error ? (
        <p role="alert" className="text-error">
          <Icon name="error" /> {error}
        </p>
      ) : null}

      {active.length === 0 ? (
        <Card>
          <CardBody>
            <p className="py-3 text-on-surface-variant">No active calls right now.</p>
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {active.map((c) => (
            <Card key={c.id}>
              <CardBody>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-medium">Callbox {c.callbox_id.slice(0, 8)}</p>
                    <p className="text-sm text-on-surface-variant">
                      Started {new Date(c.initiated_at).toLocaleTimeString()}
                    </p>
                  </div>
                  <Badge tone={statusTone(c.status)}>{statusLabel(c.status)}</Badge>
                </div>
                {c.snapshot_ids.length === 0 ? (
                  <div className="aspect-video bg-surface-container-high rounded flex items-center justify-center text-on-surface-variant">
                    <Icon name="image" /> No snapshots yet
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {c.snapshot_ids.slice(0, 3).map((sid) => (
                      <img
                        key={sid}
                        src={`/api/v1/snapshots/${sid}/image`}
                        alt="Callbox snapshot"
                        className="aspect-video object-cover rounded border border-outline-variant"
                      />
                    ))}
                  </div>
                )}
                {c.notes ? (
                  <p className="mt-3 text-sm text-on-surface-variant">
                    <span className="font-medium">Notes:</span> {c.notes}
                  </p>
                ) : null}
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
